/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

export type SoundingsColumnKey =
  | 'pressure'
  | 'temperature'
  | 'dewPoint'
  | 'height'
  | 'windSpeed'
  | 'windDirection';

export type TemperatureUnit = 'C' | 'F';
export type HeightUnit = 'm' | 'ft';
export type WindSpeedUnit = 'ms' | 'kts' | 'kmh';

export interface SoundingsProfileDataUnits {
  temperature: TemperatureUnit;
  dewPoint: TemperatureUnit;
  height: HeightUnit;
  windSpeed: WindSpeedUnit;
}

export interface SoundingsProfileDataPreferences {
  visibleColumns: Record<SoundingsColumnKey, boolean>;
  units: SoundingsProfileDataUnits;
}

const TEMPERATURE_UNIT_OPTIONS = [
  'C',
  'F',
] as const satisfies readonly TemperatureUnit[];
const HEIGHT_UNIT_OPTIONS = [
  'm',
  'ft',
] as const satisfies readonly HeightUnit[];
const WIND_SPEED_UNIT_OPTIONS = [
  'ms',
  'kts',
  'kmh',
] as const satisfies readonly WindSpeedUnit[];

const WIND_SPEED_UNIT_LABELS: Record<WindSpeedUnit, string> = {
  ms: 'm/s',
  kts: 'kts',
  kmh: 'km/h',
};

const celsiusToFahrenheit = (celsius: number): number => celsius * (9 / 5) + 32;
const metersToFeet = (meters: number): number => meters / 0.3048;
const knotsToMps = (kt: number): number => (kt * 1852) / 3600;
const knotsToKmh = (kt: number): number => kt * 1.852;

export const convertTemperature = (
  valueCelsius: number,
  unit: TemperatureUnit,
): number => (unit === 'F' ? celsiusToFahrenheit(valueCelsius) : valueCelsius);

export const convertHeight = (valueMeters: number, unit: HeightUnit): number =>
  unit === 'ft' ? metersToFeet(valueMeters) : valueMeters;

export const convertWindSpeed = (
  valueKnots: number,
  unit: WindSpeedUnit,
): number => {
  if (unit === 'ms') {
    return knotsToMps(valueKnots);
  }
  if (unit === 'kmh') {
    return knotsToKmh(valueKnots);
  }
  return valueKnots;
};

/**
 * Single source of truth for everything column-specific in the profile data
 * table: ordering, labels, units, formatting and conversion. Adding a new
 * column means adding one entry here.
 */
export interface ColumnDefinition {
  key: SoundingsColumnKey;
  /** Short header label, e.g. 'P', 'T', 'Td' */
  shortLabel: string;
  /** User-facing long label used in menus, e.g. 'Pressure' */
  longLabel: string;
  /** Which slot of `SoundingsProfileDataUnits` drives this column's unit */
  unitKey?: keyof SoundingsProfileDataUnits;
  /** Allowed unit options when the column has selectable units */
  unitOptions?: readonly string[];
  /**
   * Optional display labels for `unitOptions` — used by both the menu toggle
   * buttons and the column header when the internal identifier differs from
   * the conventional unit symbol (e.g. `kts` → `kt`, `kmh` → `km/h`). Falls
   * back to the option identifier when no mapping is provided.
   */
  unitOptionLabels?: Readonly<Record<string, string>>;
  /** How many decimals the formatted value should have */
  fractionDigits: (units: SoundingsProfileDataUnits) => number;
  /** Render-time unit label shown next to the header, e.g. '°C', 'hPa' */
  unitLabel: (units: SoundingsProfileDataUnits) => string;
  /** Convert a stored (canonical) value into the currently selected unit */
  convert: (value: number, units: SoundingsProfileDataUnits) => number;
}

export const COLUMN_DEFINITIONS: ColumnDefinition[] = [
  {
    key: 'pressure',
    shortLabel: 'P',
    longLabel: 'Pressure',
    fractionDigits: () => 0,
    unitLabel: () => 'hPa',
    convert: (value) => value,
  },
  {
    key: 'temperature',
    shortLabel: 'T',
    longLabel: 'Temperature',
    unitKey: 'temperature',
    unitOptions: TEMPERATURE_UNIT_OPTIONS,
    fractionDigits: () => 1,
    unitLabel: (units) => (units.temperature === 'F' ? '°F' : '°C'),
    convert: (value, units) => convertTemperature(value, units.temperature),
  },
  {
    key: 'dewPoint',
    shortLabel: 'Td',
    longLabel: 'Dew point',
    unitKey: 'dewPoint',
    unitOptions: TEMPERATURE_UNIT_OPTIONS,
    fractionDigits: () => 1,
    unitLabel: (units) => (units.dewPoint === 'F' ? '°F' : '°C'),
    convert: (value, units) => convertTemperature(value, units.dewPoint),
  },
  {
    key: 'height',
    shortLabel: 'Z',
    longLabel: 'Height',
    unitKey: 'height',
    unitOptions: HEIGHT_UNIT_OPTIONS,
    fractionDigits: () => 0,
    unitLabel: (units) => units.height,
    convert: (value, units) => convertHeight(value, units.height),
  },
  {
    key: 'windSpeed',
    shortLabel: 'WS',
    longLabel: 'Wind speed',
    unitKey: 'windSpeed',
    unitOptions: WIND_SPEED_UNIT_OPTIONS,
    unitOptionLabels: WIND_SPEED_UNIT_LABELS,
    fractionDigits: (units) => (units.windSpeed === 'ms' ? 1 : 0),
    unitLabel: (units) => WIND_SPEED_UNIT_LABELS[units.windSpeed],
    convert: (value, units) => convertWindSpeed(value, units.windSpeed),
  },
  {
    key: 'windDirection',
    shortLabel: 'WD',
    longLabel: 'Wind direction',
    fractionDigits: () => 0,
    unitLabel: () => '°',
    convert: (value) => value,
  },
];

const COLUMN_DEFINITION_BY_KEY: Record<SoundingsColumnKey, ColumnDefinition> =
  Object.fromEntries(COLUMN_DEFINITIONS.map((def) => [def.key, def])) as Record<
    SoundingsColumnKey,
    ColumnDefinition
  >;

export const getColumnDefinition = (
  key: SoundingsColumnKey,
): ColumnDefinition => COLUMN_DEFINITION_BY_KEY[key];

export const COLUMN_ORDER: SoundingsColumnKey[] = COLUMN_DEFINITIONS.map(
  (def) => def.key,
);

export const DEFAULT_PROFILE_DATA_PREFERENCES: SoundingsProfileDataPreferences =
  {
    visibleColumns: {
      pressure: true,
      temperature: true,
      dewPoint: true,
      height: true,
      windSpeed: true,
      windDirection: true,
    },
    units: {
      temperature: 'C',
      dewPoint: 'C',
      height: 'm',
      windSpeed: 'ms',
    },
  };
